import { Category } from '../Components/Utils/SelectionPane/SelectionPane';
import { FileData } from '../Components/Utils/FileItem/FileItem';

export interface SelectedDiagnostic {
  id: string;
  title: string;
  section: string;
}

function formatDiagnosticSection(section: string, categories: Category<string>[]): string {
  const selected = categories.flatMap((cat) =>
    cat.data
      .filter((item) => item.selected)
      .map((item) => `- ${item.text}`)
  );

  if (selected.length === 0) {
    return '';
  }

  return `${section}:\n${selected.join('\n')}`;
}

export function collectSelectedDiagnostics(
  categories: Category<string>[],
  improveCategories: Category<string>[],
  developCategories: Category<string>[]
): SelectedDiagnostic[] {
  const fromSection = (section: string, cats: Category<string>[]) =>
    cats.flatMap((cat) =>
      cat.data
        .filter((item) => item.selected)
        .map((item) => ({
          id: item.id,
          title: item.text,
          section: `${section} / ${cat.title}`,
        }))
    );

  return [
    ...fromSection('Evaluate', categories),
    ...fromSection('Track', improveCategories),
    ...fromSection('Develop', developCategories),
  ];
}

export function collectSelectedActivityIds(
  categories: Category<string>[],
  improveCategories: Category<string>[],
  developCategories: Category<string>[]
): string[] {
  return collectSelectedDiagnostics(categories, improveCategories, developCategories).map(
    (item) => item.id
  );
}

function formatEvidenceLines(evidenceFiles: FileData[]): string {
  if (evidenceFiles.length === 0) {
    return 'None selected.';
  }

  return evidenceFiles
    .map((file) => {
      const source = file.source === 'uploaded' ? 'uploaded' : 'library';
      return `- ${file.fileName} (fileId: ${file.id}, source: ${source})`;
    })
    .join('\n');
}

export function buildAnalysePromptFromCategories(
  userPrompt: string,
  categories: Category<string>[],
  improveCategories: Category<string>[],
  developCategories: Category<string>[],
  evidenceFiles: FileData[]
): string {
  // Legacy wrapper kept for callers that still need a single blob; prefer buildAnalyseInstructions.
  const instructions = buildAnalyseInstructions(
    categories,
    improveCategories,
    developCategories,
    evidenceFiles
  );
  return `${instructions}\n\nUser question:\n${userPrompt.trim()}`;
}

export interface AnalyseInstructionsPayload {
  instructions: string;
  activityIds: string[];
  selectionSummary: string;
  evidenceSummary: string;
}

/** Builds per-request Analyse scope for ChatRequest.instructions / analyseContext — not the user message. */
export function buildAnalyseInstructions(
  categories: Category<string>[],
  improveCategories: Category<string>[],
  developCategories: Category<string>[],
  evidenceFiles: FileData[]
): AnalyseInstructionsPayload {
  const evaluateSection = formatDiagnosticSection('Evaluate', categories);
  const trackSection = formatDiagnosticSection('Track', improveCategories);
  const developSection = formatDiagnosticSection('Develop', developCategories);

  const diagnosticBlocks = [evaluateSection, trackSection, developSection]
    .filter(Boolean)
    .join('\n\n');

  const activityIds = collectSelectedActivityIds(
    categories,
    improveCategories,
    developCategories
  );

  const evidenceLines = formatEvidenceLines(evidenceFiles);
  const hasEvidence = evidenceFiles.length > 0;

  const instructions =
    activityIds.length > 0
      ? `The user has already selected the diagnostics/plans listed below. That selection is the complete scope — do not ask which lists to include, whether to fetch records, or for permission to proceed.
Use the get_analyse_activities_context tool with these activityIds (do not paste this JSON or narrate the tool call in your reply): ${JSON.stringify(activityIds)}
These are platform diagnostics/plans, not uploaded files. Use the tool response as your source of truth and answer the user's question in full in this turn.
Never output tool names, tool-call JSON, activityIds, or GUIDs in the user-facing reply.
Do not call get_user_library_files unless the user explicitly asks to search the wider library. Never summarise a file that is not listed under Evidence files available unless the user named that file.
${hasEvidence ? 'Also use the selected evidence files below. Read them with ONE retrieve_library_files_content call that includes every fileId (the server loads them in parallel). Do not call the tool once per file. Do not ask whether to use them.' : ''}
When referring to a diagnostic or plan, use its title — never its ID or GUID.
Do not ask clarifying questions unless the request is genuinely ambiguous after you have loaded the data. Prefer a clear, useful default answer over follow-up questions.
Do not mention the client or organisation name unless the user explicitly asks for it — data is already scoped to their client.
Write as an education, skills, and employability quality consultant. Avoid agile/tech jargon such as sprint, standup, or backlog. Prefer action plan, review cycle, improvement activity, timescale, and next steps.
You can produce in-chat text only (prose, markdown tables, a one-page brief, slide-ready bullets). Do not claim to create, attach, or export spreadsheets, PowerPoint, PDFs, or other files. If asked for a spreadsheet, give a markdown table or CSV in a code block and say a downloadable Excel file cannot be produced here.

Selected diagnostics and plans:
${diagnosticBlocks || 'None selected.'}

Evidence files available:
${evidenceLines}`
      : `No diagnostics or plans were selected. Ask the user to select assets in the context panel, or use uploaded/library evidence if available.
Do not call get_user_library_files unless the user explicitly asks to search the wider library. Never summarise a file that is not listed under Evidence files available unless the user named that file.
Write as an education, skills, and employability quality consultant. Avoid agile/tech jargon such as sprint, standup, or backlog.
You can produce in-chat text only. Do not claim to create, attach, or export spreadsheets, PowerPoint, PDFs, or other files.

Selected diagnostics and plans:
None selected.

Evidence files available:
${evidenceLines}`;

  return {
    instructions,
    activityIds,
    selectionSummary: diagnosticBlocks || 'None selected.',
    evidenceSummary: evidenceLines,
  };
}
